import {
    Column,
    Container,
    Image,
    MaterialContext,
    Panel,
    PanelProps,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {MenuButton} from '../components/menu-button.js';
import {Align, FlexDirection, Justify, PositionType} from '@wonderlandengine/react-ui';
import {colorSwatch} from '../utils/colorSwatch.js';
import {WindowHeader} from '../components/window-header.js';
import {Window} from '../components/window.js';
import {useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {MalUI} from '../components/mal-ui.js';
import {ColoredImage} from '../components/colored-image.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {MalSpeech, TailDirection} from '../components/mal-speech.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {Localization} from '../../utils/Localization.js';
import {GameFlowController, TutorialPhase} from '../../components/game-flow-controller.js';
import {UIModel} from '../../models/UIModel.js';
import {UIText} from '../../types/materials.js';
import {parseColor} from '../utils/utils.js';

let SpeechTextMaterial: UIText | null = null;

const useTutorial = (): [string, string, () => void] => {
    const currentPhase = useRef<TutorialPhase>(UIModel.instance.currentTutorialPhase);

    const [textIndex, setTextIndex] = useState(0);
    const [text, setText] = useState(
        currentPhase?.current?.interstitialText[textIndex] ?? ''
    );
    const [title, setTitle] = useState(currentPhase?.current?.interstitialTitle ?? '');

    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const speechTextMaterial = useMemo(() => {
        if (SpeechTextMaterial === null) {
            SpeechTextMaterial = rotbMats?.regularTextMaterial?.clone() as UIText;
            SpeechTextMaterial.setColor(parseColor(colorSwatch.UiDarkText));
        }
        return SpeechTextMaterial;
    }, []);
    useLayoutEffect(() => {
        if (currentPhase.current == null) {
            return;
        }
        setText(
            Localization.instance.get(currentPhase.current.interstitialText[textIndex])
        );
        setTitle(Localization.instance.get(currentPhase.current.interstitialTitle));
    }, [textIndex]);

    useEffect(() => {
        const phaseChanged = (change: string) => {
            if (change != 'TutorialPhase') {
                return;
            }
            currentPhase.current = UIModel.instance.currentTutorialPhase;
            setTextIndex(0);
            setText(Localization.instance.get(currentPhase.current.interstitialText[0]));
            setTitle(Localization.instance.get(currentPhase.current.interstitialTitle));
        };
        UIModel.instance.propertyChanged.add(phaseChanged);
        //phaseChanged('TutorialPhase');
        return () => {
            UIModel.instance.propertyChanged.remove(phaseChanged);
        };
    }, []);

    const disableButton = useRef(false);

    const onClick = () => {
        if (disableButton.current) {
            return;
        }
        disableButton.current = true;
        setTimeout(() => {
            disableButton.current = false;
        }, 500);

        if (currentPhase.current == null) {
            return;
        }
        const next = textIndex + 1;
        if (next >= currentPhase.current.interstitialText.length) {
            // close tutorial
            GameFlowController.instance.tutorialPhaseComplete();
            return;
        }
        setTextIndex(next);
    };

    return [text, title, onClick];
};

const TutorialMal = (props: {text; panelMaterial; circleTexture}) => {
    return (
        <Panel material={props.panelMaterial!} width={510} height={510} rounding={0}>
            <Container
                alignSelf={Align.Center}
                height={240}
                //position={PositionType.Absolute}
                justifyContent={Justify.Center}
                alignItems={Align.Center}
            >
                <MalSpeech
                    width="auto"
                    maxHeight={240}
                    maxWidth={475}
                    direction={TailDirection.down}
                >
                    {props.text}
                </MalSpeech>
            </Container>
            <Image
                position={PositionType.Absolute}
                margin={5}
                rounding={0}
                src={UiTexturesManager.instance.uiBgIsland}
                width={500}
                height={500}
            />
            {/* <Container top={-500}>
        <MalUI
            top={115}
            alignSelf={Align.Center}
            width={300}
            height={300}
        ></MalUI>
    </Container> */}
            <Container position={PositionType.Absolute}>
                <MalUI
                    top={120}
                    left={55}
                    alignSelf={Align.Center}
                    width={300}
                    height={300}
                ></MalUI>
                <ColoredImage
                    color={'#00000090'}
                    src={props.circleTexture}
                    width={190}
                    height={20}
                    top={80}
                    left={165}
                />
            </Container>
        </Panel>
    );
};

const TutorialImage = (props: {image; panelMaterial}) => {
    return (
        <Container>
            <Panel material={props.panelMaterial!} width={510} height={510} rounding={0}>
                <Image margin={5} rounding={0} src={props.image} width={500} height={500} />
            </Panel>
        </Container>
    );
};

export const Tutorial = (props: React.PropsWithChildren<PanelProps> & {}) => {
    const mat = useContext(MaterialContext);
    const mats = useRotBMaterialContext();

    const [text, title, onClick] = useTutorial();

    return (
        <Container
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Row}
            justifyContent={Justify.Center}
        >
            <Column marginTop={36}>
                <Window>
                    <Column
                        height="100%"
                        marginTop={20}
                        alignSelf={Align.Center}
                        width={500}
                        rowGap={20}
                    >
                        <WindowHeader title={title} />

                        <Container flexGrow={1} marginTop={50}>
                            <TutorialMal
                                text={text}
                                panelMaterial={mat.panelMaterial}
                                circleTexture={mats.circleTexture}
                            ></TutorialMal>
                        </Container>
                    </Column>
                </Window>
                <Row justifyContent={Justify.FlexEnd} marginRight={15}>
                    <MenuButton
                        width={200}
                        height={54}
                        margin={6}
                        color={colorSwatch.AcceptButton}
                        fontSize={22}
                        onClick={onClick}
                        icon={UiTexturesManager.instance.arrow}
                        alignIcon="right"
                        text={Localization.instance.get('Next')}
                    ></MenuButton>
                </Row>
            </Column>
        </Container>
    );
};
